/** NOTE: This component is shared with MKP!
 * Prevent regressions by spoofing as a tenant when testing. */
import React, { Component, Fragment } from 'react';
import PropTypes from 'prop-types';
import { inject, observer } from 'mobx-react';
import storeLoader from 'app/stores/storeLoader';
import { isEqual, noop } from 'lodash';
import formatMetricValue from 'app/util/formatMetricValue';

import { AiFillBug, AiOutlineComment } from 'react-icons/ai';
import { getInvestigationStatusConfig } from 'app/stores/ai/investigationStatus';

import { formatDateTime, DEFAULT_DATETIME_FORMAT_WITH_SECONDS } from 'core/util/dateUtils';
import { Box, Flex, Link, MenuDivider, Tag, Text, Tooltip } from 'core/components';
import DashboardLink from 'app/components/links/DashboardLink';
import IdTag, { formatDisplayId } from 'app/components/IdTag';
import AdminTable from 'app/components/admin/AdminTable';
import ClearAlertDialog from 'app/views/alerting/components/alertActions/ClearAlertDialog';
import TargetRenderer from 'app/views/protect/ddos/analyze/components/TargetRenderer';
import UnackAlertButton from 'app/views/alerting/components/alertActions/UnackAlertButton';
import AckAlertDialog from 'app/views/alerting/components/alertActions/AckAlertDialog';

import SilenceAlertButton from 'app/views/alerting/components/alertActions/SilenceAlertButton';
import SuppressAlertButton from 'app/views/alerting/components/alertActions/SuppressAlertButton';
import CommentDialog from 'app/components/comments/CommentDialog';

import { metricLabelRenderer } from 'app/components/metric/metricRenderers';
import MitigationLink from 'app/components/links/MitigationLink';
import AlertDebugDialog from 'app/views/alerting/components/alertDebugger/AlertDebugDialog';
import AlertSeverityBars from 'app/views/alerting/components/AlertSeverityBars';
import { correlationPath } from 'app/views/alerting/correlations/links';
import { ALERT_TABLE_COLUMNS } from '@kentik/ui-shared/alerting/constants';

import NmsNativeAlertDimensionRenderer from './nms/NmsNativeAlertDimensionRenderer';
import AckStateLabel from './AckStateLabel';
import NmsNativeAlertMetricListRenderer from './nms/NmsNativeAlertMetricListRenderer';
import AlertStatusTag from './AlertStatusTag';

const ROW_HEIGHT = 60;
const CONTEXT_NAME_ALERT_TABLE = 'Alert Table';
const CONTEXT_TYPE_ALERT_TABLE = 'alert_table';

@storeLoader('$alerting.policyCollection')
@inject('$app', '$auth', '$mkp', '$exports', '$comments', '$suppressions', '$users', '$kaizen')
@observer
class AlertingTable extends Component {
  static propTypes = {
    onRowClick: PropTypes.func,
    showTenantAlerts: PropTypes.bool,
    tableProps: PropTypes.object,
    useVirtualizedTable: PropTypes.bool,
    // Columns most tables don't need (ALERT_TABLE_COLUMNS.SUPPRESSED/ARCHIVED); only those listed exist on this table.
    additionalColumns: PropTypes.arrayOf(PropTypes.string),
    sortable: PropTypes.bool
  };

  static defaultProps = {
    onRowClick: noop,
    showTenantAlerts: false,
    tableProps: {},
    useVirtualizedTable: true,
    additionalColumns: [],
    sortable: true
  };

  state = {
    commentModel: null,
    debugModel: null,
    clearAlertModel: null,
    loadingSuppressions: true,
    loadingSilences: true
  };

  get isEmbedded() {
    const { collection } = this.props;

    return Boolean(collection);
  }

  componentDidMount() {
    const { $app, $auth, $suppressions, $alerting, $exports, $users } = this.props;

    if (this.isEmbedded) {
      this.setState({ loadingSuppressions: false, loadingSilences: false });
      return;
    }

    $exports.getSettings().then(({ sortState }) => {
      if (sortState && !isEqual($alerting.collection.sortState, sortState)) {
        $alerting.collection.serverSort(sortState.field, sortState.direction);
      }
    });

    if ($app.isSubtenant) {
      this.setState({ loadingSuppressions: false, loadingSilences: false });
    } else {
      const suppressions = $auth.hasRbacPermissions(['alerting.suppressions::read'])
        ? $suppressions.collection.fetch()
        : Promise.resolve();
      suppressions
        .catch((error) => console.error('Unable to load suppressions', error))
        .finally(() => this.setState({ loadingSuppressions: false }));
      const silences = $auth.hasRbacPermissions(['alerting.silences::read'])
        ? $alerting.silenceCollection.fetch()
        : Promise.resolve();
      silences
        .catch((error) => console.error('Unable to load silences', error))
        .finally(() => this.setState({ loadingSilences: false }));
      $users.collection.fetch();
    }

    // Initialize AI context
    this.updateTableContext();
  }

  componentDidUpdate(prevProps) {
    const { collection, $alerting } = this.props;

    if (this.isEmbedded) {
      return;
    }

    const activeCollection = collection || $alerting.collection;
    const prevCollection = prevProps.collection || prevProps.$alerting.collection;

    // Update context if collection size or content changed
    if (activeCollection.size !== prevCollection.size || activeCollection !== prevCollection) {
      this.updateTableContext();
    }
  }

  componentWillUnmount() {
    const { $kaizen } = this.props;

    if (this.isEmbedded) {
      return;
    }

    // Clean up table context
    const tableContext = Array.from($kaizen.activeChatContext).find((ctx) => ctx.type === CONTEXT_TYPE_ALERT_TABLE);
    if (tableContext) {
      $kaizen.removeChatContext(tableContext);
    }
  }

  /**
   * Updates the AI assistant context with current alert table state.
   * Provides comprehensive information about visible alerts, their severity, and status.
   */
  updateTableContext = () => {
    const { $kaizen, collection, $alerting, $app } = this.props;

    const activeCollection = collection || $alerting.collection;

    const totalAlerts = activeCollection.size;
    const alerts = activeCollection.models || [];

    if (totalAlerts === 0) {
      // Update minimal context when no alerts
      $kaizen.updateChatContext({
        type: CONTEXT_TYPE_ALERT_TABLE,
        name: CONTEXT_NAME_ALERT_TABLE,
        context: 'Alert Table\nState: OPEN\n\nNo alerts currently visible.',
        placeholder: 'Ask about alerts',
        hidden: true
      });
      return;
    }

    // Analyze alert collection
    const severityBreakdown = {};
    const stateBreakdown = {};
    const typeBreakdown = {};
    const policyBreakdown = {};
    let ackedCount = 0;
    let silencedCount = 0;
    let activeCount = 0;
    let clearedCount = 0;

    alerts.forEach((alert) => {
      const severity = alert.get('severity') || 'Unknown';
      const state = alert.get('stateLabel') || 'Unknown';
      const type = alert.topLevelApplication || 'Unknown';
      const policyName = alert.get('policy.name') || 'Unknown';

      severityBreakdown[severity] = (severityBreakdown[severity] || 0) + 1;
      stateBreakdown[state] = (stateBreakdown[state] || 0) + 1;
      typeBreakdown[type] = (typeBreakdown[type] || 0) + 1;

      // Track top policies (limit to prevent excessive context)
      if (Object.keys(policyBreakdown).length < 10) {
        policyBreakdown[policyName] = (policyBreakdown[policyName] || 0) + 1;
      }

      if (alert.isAcked) {
        ackedCount += 1;
      }
      if (alert.get('silenceModel')) {
        silencedCount += 1;
      }
      if (alert.isActive) {
        activeCount += 1;
      }
      if (alert.isCleared) {
        clearedCount += 1;
      }
    });

    // Build context parts
    const contextParts = [
      'ALERT TABLE',
      'State: OPEN',
      `\nTOTAL ALERTS: ${totalAlerts}`,
      `Active: ${activeCount}`,
      `Cleared: ${clearedCount}`,
      !$app.isSubtenant && ackedCount > 0 ? `Acknowledged: ${ackedCount}` : null,
      !$app.isSubtenant && silencedCount > 0 ? `Silenced: ${silencedCount}` : null
    ].filter(Boolean);

    // Add severity breakdown
    const sortedSeverities = Object.entries(severityBreakdown).sort(([, a], [, b]) => b - a);
    if (sortedSeverities.length > 0) {
      contextParts.push('\nSEVERITY BREAKDOWN:');
      sortedSeverities.forEach(([severity, count]) => {
        contextParts.push(`  ${severity}: ${count} alert${count !== 1 ? 's' : ''}`);
      });
    }

    // Add type breakdown
    const sortedTypes = Object.entries(typeBreakdown).sort(([, a], [, b]) => b - a);
    if (sortedTypes.length > 0) {
      contextParts.push('\nALERT TYPES:');
      sortedTypes.forEach(([type, count]) => {
        contextParts.push(`  ${type}: ${count} alert${count !== 1 ? 's' : ''}`);
      });
    }

    // Add top policies
    const sortedPolicies = Object.entries(policyBreakdown).sort(([, a], [, b]) => b - a);
    if (sortedPolicies.length > 0) {
      contextParts.push('\nTOP POLICIES:');
      sortedPolicies.slice(0, 5).forEach(([policy, count]) => {
        contextParts.push(`  ${policy}: ${count} alert${count !== 1 ? 's' : ''}`);
      });
      if (sortedPolicies.length > 5) {
        contextParts.push(`  ...and ${sortedPolicies.length - 5} more policies`);
      }
    }

    // Add sample of recent alerts (top 3)
    if (alerts.length > 0) {
      contextParts.push('\nRECENT ALERTS (sample):');
      alerts.slice(0, 3).forEach((alert, index) => {
        const summary = this.getAlertSummary(alert);
        contextParts.push(`${index + 1}. ${summary}`);
      });
      if (alerts.length > 3) {
        contextParts.push(`...and ${alerts.length - 3} more alerts`);
      }
    }

    // Update the context
    $kaizen.updateChatContext({
      type: CONTEXT_TYPE_ALERT_TABLE,
      name: CONTEXT_NAME_ALERT_TABLE,
      context: contextParts.join('\n'),
      placeholder: 'Ask about alerts',
      hidden: true
    });
  };

  /**
   * Builds a meaningful summary for an alert for AI context.
   * Used by AdminTable's context tracking.
   */
  getAlertSummary = (model) => {
    const parts = [];

    const policyName = model.get('policy.name');
    const severity = model.get('severity');
    const stateLabel = model.get('stateLabel');
    const application = model.topLevelApplication;
    const startTime = model.get('start_time');
    const duration = model.displayDuration;

    if (policyName) {
      parts.push(`Policy: ${policyName}`);
    }
    if (severity) {
      parts.push(`Severity: ${severity}`);
    }
    if (stateLabel) {
      parts.push(`State: ${stateLabel}`);
    }
    if (application) {
      parts.push(`Type: ${application}`);
    }
    if (startTime) {
      parts.push(`Started: ${formatDateTime(startTime, DEFAULT_DATETIME_FORMAT_WITH_SECONDS)}`);
    }
    if (duration) {
      parts.push(`Duration: ${duration}`);
    }

    // Add ack state if applicable
    if (model.isAcked) {
      parts.push(model.isAutoAcked ? 'Auto-acknowledged' : 'Acknowledged');
    }

    // Add silence state if applicable
    if (model.get('silenceModel')) {
      parts.push('Silenced');
    }

    return parts.join(', ');
  };

  get defaultColumns() {
    const { $app, $auth, additionalColumns } = this.props;

    const defaultColumns = ['severity', 'stateLabel', 'type', 'policyName', 'primaryDimension', 'metric', 'startTime'];

    if (!$app.isSubtenant) {
      defaultColumns.push('silenceModel', 'ackStateLabel');
    }

    if ($auth.canViewCorrelations && additionalColumns.includes(ALERT_TABLE_COLUMNS.CORRELATION)) {
      defaultColumns.push(ALERT_TABLE_COLUMNS.CORRELATION);
    }

    return defaultColumns;
  }

  get columns() {
    const { $auth, $app, $mkp, showTenantAlerts, hideColumns, additionalColumns, sortable } = this.props;

    let columns = [
      {
        label: 'Severity',
        name: ALERT_TABLE_COLUMNS.SEVERITY,
        id: ALERT_TABLE_COLUMNS.SEVERITY,
        value: ALERT_TABLE_COLUMNS.SEVERITY,
        width: 120,
        ellipsis: false,
        serverSortable: true,
        renderer: ({ value }) => <AlertSeverityBars severity={value} small showLabel />
      },

      {
        label: 'Alert State',
        name: ALERT_TABLE_COLUMNS.STATE_LABEL,
        id: ALERT_TABLE_COLUMNS.STATE_LABEL,
        value: ALERT_TABLE_COLUMNS.STATE_LABEL,
        computed: true,
        width: 90,
        ellipsis: false,
        serverSortable: true,
        sortField: 'state',
        renderer: ({ model }) => <AlertStatusTag isActive={model.isActive} />
      },

      {
        label: 'Type',
        name: ALERT_TABLE_COLUMNS.APPLICATION,
        id: ALERT_TABLE_COLUMNS.APPLICATION,
        value: ALERT_TABLE_COLUMNS.APPLICATION,
        computed: true,
        width: 100,
        ellipsis: false,
        serverSortable: true,
        renderer: ({ value }) => (value ? <Tag>{value}</Tag> : null)
      },

      {
        label: 'Policy',
        name: ALERT_TABLE_COLUMNS.POLICY_NAME,
        id: ALERT_TABLE_COLUMNS.POLICY_NAME,
        value: ALERT_TABLE_COLUMNS.POLICY_NAME,
        computed: true,
        flexBasis: 150,
        minWidth: 150,
        serverSortable: true,
        uiSortLogic: true
      },

      {
        label: 'Policy ID',
        name: ALERT_TABLE_COLUMNS.POLICY_ID,
        id: ALERT_TABLE_COLUMNS.POLICY_ID,
        value: ALERT_TABLE_COLUMNS.POLICY_ID,
        flexBasis: 80,
        minWidth: 80,
        serverSortable: true,
        uiSortLogic: true,
        renderer: ({ model }) => <IdTag id={model.get('policy.id')} small isExport={$app.isExport} />
      },

      {
        label: 'Dimensions',
        name: ALERT_TABLE_COLUMNS.PRIMARY_DIMENSION,
        id: ALERT_TABLE_COLUMNS.PRIMARY_DIMENSION,
        value: ALERT_TABLE_COLUMNS.PRIMARY_DIMENSION,
        sortField: 'primary_dimension',
        flexBasis: 200,
        minWidth: 200,
        computed: true,
        ellipsis: false,
        serverSortable: false,
        sortable: false,
        renderer: ({ model }) =>
          model.isNmsPolicy ? (
            <NmsNativeAlertDimensionRenderer maxDimensionsToShow={3} alertModel={model} />
          ) : (
            <TargetRenderer model={model} />
          )
      },

      {
        label: 'Metric',
        name: ALERT_TABLE_COLUMNS.METRIC,
        id: ALERT_TABLE_COLUMNS.METRIC,
        value: ALERT_TABLE_COLUMNS.METRIC,
        sortable: false,
        ellipsis: false,
        flexBasis: 200,
        minWidth: 150,
        flexDirection: 'column',
        alignItems: 'flex-start',
        renderer: ({ model }) => (
          <Flex flexDirection="column" gap="4px" alignItems="flex-start">
            {this.renderMetricColumn(model)}
          </Flex>
        )
      },

      {
        label: 'Mitigation ID',
        name: ALERT_TABLE_COLUMNS.MITIGATION_ID,
        id: ALERT_TABLE_COLUMNS.MITIGATION_ID,
        value: ALERT_TABLE_COLUMNS.MITIGATION_ID,
        computed: true,
        flexBasis: 50,
        minWidth: 118,
        ellipsis: false,
        sortable: false,
        serverSortable: false,
        renderer: ({ value }) =>
          value ? (
            <MitigationLink mb={1} intent="primary" small blank id={value} isSubtenant={$app.isSubtenant}>
              {value}
            </MitigationLink>
          ) : (
            <Text muted>None</Text>
          )
      },

      {
        label: 'Alert ID',
        name: ALERT_TABLE_COLUMNS.ID,
        id: ALERT_TABLE_COLUMNS.ID,
        value: ALERT_TABLE_COLUMNS.ID,
        flexBasis: 175,
        minWidth: 175,
        ellipsis: false,
        serverSortable: true,
        sortField: 'alarm_id',
        renderer: ({ value }) => <IdTag id={value} small isExport={$app.isExport} />
      },
      {
        label: `Time (${$auth.userTimezone})`,
        name: ALERT_TABLE_COLUMNS.START_TIME,
        id: ALERT_TABLE_COLUMNS.START_TIME,
        value: ALERT_TABLE_COLUMNS.START_TIME,
        flexBasis: 60,
        minWidth: 180,
        ellipsis: false,
        flexDirection: 'column',
        alignItems: 'stretch',
        sortField: 'start_time',
        serverSortable: true,
        renderer: ({ model }) => {
          const startTime = model.get('start_time');
          const endTime = model.get('end_time');

          return (
            <>
              <Flex gap="4px">
                <Text muted>Start:</Text>
                <Text>{formatDateTime(startTime, DEFAULT_DATETIME_FORMAT_WITH_SECONDS)}</Text>
              </Flex>

              {model.isCleared && (
                <Flex gap="4px">
                  <Text muted>Clear: </Text>
                  {endTime && <Text>{formatDateTime(endTime, DEFAULT_DATETIME_FORMAT_WITH_SECONDS)}</Text>}
                </Flex>
              )}
              <Flex gap="4px">
                <Text as="div" muted>
                  Duration:
                </Text>
                <Text as="div">{model.displayDuration}</Text>
              </Flex>
            </>
          );
        }
      }
    ];

    if (!$app.isSubtenant) {
      columns.push(
        ...[
          {
            label: 'Tenant',
            name: ALERT_TABLE_COLUMNS.TENANT,
            id: ALERT_TABLE_COLUMNS.TENANT,
            value: ALERT_TABLE_COLUMNS.TENANT,
            computed: true,
            flexBasis: 80,
            minWidth: 150,
            sortable: false,
            hidden: !showTenantAlerts || $mkp.tenants.tenantAlertingOptions.length === 0
          },
          {
            label: 'Silence State',
            name: ALERT_TABLE_COLUMNS.SILENCE_MODEL,
            id: ALERT_TABLE_COLUMNS.SILENCE_MODEL,
            value: ALERT_TABLE_COLUMNS.SILENCE_MODEL,
            computed: true,
            sortable: false,
            width: 160,
            renderer: ({ value }) => {
              const silenceModel = value;

              if (silenceModel) {
                const { startTime, startDetails = {}, expirationDetails = {} } = silenceModel;
                return (
                  <Box>
                    <Text as="div">
                      Silence
                      {startDetails.started ? `d for ${expirationDetails.duration}` : ' pending'}
                    </Text>
                    {startDetails.started && (
                      <Text as="div" muted>
                        Started: {startTime}
                      </Text>
                    )}
                    {!startDetails.started && (
                      <Text as="div" muted>
                        Starts: {startTime}
                      </Text>
                    )}

                    <Text as="div" muted>
                      Ends: {expirationDetails.expirationDay}
                    </Text>
                  </Box>
                );
              }

              return 'Not Silenced';
            }
          },
          {
            label: 'Ack State',
            name: ALERT_TABLE_COLUMNS.ACK_STATE_LABEL,
            id: ALERT_TABLE_COLUMNS.ACK_STATE_LABEL,
            value: ALERT_TABLE_COLUMNS.ACK_STATE_LABEL,
            computed: true,
            sortable: false,
            flexBasis: 100,
            minWidth: 130,
            renderer: ({ model, value }) => (
              <AckStateLabel
                label={model.isAutoAcked ? 'Auto-acked' : value}
                isAcked={model.isAcked}
                userId={model.ackUserId}
                timestamp={model.ackTimestamp}
              />
            )
          },
          {
            label: 'AI Investigation',
            name: ALERT_TABLE_COLUMNS.AI_INVESTIGATION,
            id: ALERT_TABLE_COLUMNS.AI_INVESTIGATION,
            value: ALERT_TABLE_COLUMNS.AI_INVESTIGATION,
            computed: true,
            sortable: false,
            flexBasis: 100,
            minWidth: 180,
            ellipsis: false,
            flexDirection: 'column',
            alignItems: 'stretch',
            renderer: ({ model }) => {
              const aiCtx = model.externalContexts?.find((c) => c.ai_investigation)?.ai_investigation;

              if (!aiCtx?.investigation_id) {
                return null;
              }

              const statusConfig = getInvestigationStatusConfig(aiCtx.status);

              if (aiCtx.short_summary) {
                return (
                  <Tooltip
                    content={<Box maxWidth={300}>{aiCtx.short_summary}</Box>}
                    position="top"
                    targetProps={{ style: { display: 'block', overflow: 'hidden' } }}
                  >
                    <Text as="div" muted small ellipsis>
                      {aiCtx.short_summary}
                    </Text>
                  </Tooltip>
                );
              }

              return (
                <Tag intent={statusConfig.intent} small>
                  {statusConfig.label}
                </Tag>
              );
            }
          }
        ]
      );
    }

    const flagLabel =
      (label) =>
      ({ value }) =>
        value ? label : `Not ${label}`;
    columns = columns.concat(
      [
        {
          label: 'Suppressed',
          name: ALERT_TABLE_COLUMNS.SUPPRESSED,
          id: ALERT_TABLE_COLUMNS.SUPPRESSED,
          value: ALERT_TABLE_COLUMNS.SUPPRESSED,
          computed: true,
          sortable: false,
          width: 120,
          renderer: flagLabel('Suppressed')
        },
        {
          label: 'Archived',
          name: ALERT_TABLE_COLUMNS.ARCHIVED,
          id: ALERT_TABLE_COLUMNS.ARCHIVED,
          value: ALERT_TABLE_COLUMNS.ARCHIVED,
          computed: true,
          sortable: false,
          width: 120,
          renderer: flagLabel('Archived')
        },
        ...($auth.canViewCorrelations
          ? [
              {
                label: 'AI Correlation',
                name: ALERT_TABLE_COLUMNS.CORRELATION,
                id: ALERT_TABLE_COLUMNS.CORRELATION,
                value: ALERT_TABLE_COLUMNS.CORRELATION,
                computed: true,
                sortable: false,
                width: 160,
                renderer: ({ value }) =>
                  value ? (
                    <Link to={correlationPath(value)} monospace title={value}>
                      {formatDisplayId(value)}
                    </Link>
                  ) : (
                    <Text muted>None</Text>
                  )
              }
            ]
          : [])
      ].filter((column) => additionalColumns.includes(column.id))
    );

    if (hideColumns) {
      columns = columns.filter((column) => !hideColumns.includes(column.id));
    }

    if (!sortable) {
      columns = columns.map((column) => ({ ...column, sortable: false }));
    }

    return columns;
  }

  get kebabActions() {
    const { $app, miniKebab } = this.props;
    const { loadingSuppressions, loadingSilences } = this.state;

    const viewDetails = {
      id: 'view-details',
      label: 'View Alert Details',
      icon: 'zoom-in',
      handler: this.handleViewDetails
    };

    if ($app.isExport) {
      return undefined;
    }

    if ($app.isSubtenant || miniKebab) {
      return [viewDetails];
    }

    return [
      viewDetails,
      {
        id: 'divider1',
        renderer: () => <MenuDivider key="divider1" />
      },
      {
        id: 'acknowledge',
        hidden: (model) => !model.canAck || model.isAcked,
        icon: 'confirm',
        label: 'Ack Alert',
        handler: this.handleAckAlertDialog
      },
      {
        id: 'clear',
        hidden: (model) => model.isCleared,
        icon: 'tick',
        label: 'Clear Alert...',
        handler: this.handleClearAlertDialog
      },
      {
        id: 'unacknowledge',
        hidden: (model) => !model.canAck || !model.isAcked,
        renderer: (model) => <UnackAlertButton model={model} asMenuItem key="unacknowledge" />
      },
      {
        id: 'silence',
        hidden: (model) => !model.canSilence || loadingSilences,
        renderer: (model) => <SilenceAlertButton alertModel={model} asMenuItem key="silence" />
      },
      {
        id: 'suppress',
        hidden: (model) => !model.canSuppress || loadingSuppressions,
        renderer: (model) => (
          <SuppressAlertButton
            key="suppress"
            mr={0}
            alertModel={model}
            asMenuItem
            comment="Alert"
            buttonProps={{ icon: null }}
          />
        )
      },
      {
        id: 'comment',
        icon: AiOutlineComment,
        label: 'Add Comment',
        handler: this.onHandleComment
      },
      {
        id: 'divider2',
        renderer: () => <MenuDivider key="divider2" />,
        hidden: (model) => model.isMetricPolicy || model.isNmsPolicy
      },
      {
        id: 'view-dashboard',
        label: 'Open Dashboard',
        hidden: (model) => {
          const policy = model.get('policy');

          return model.isMetricPolicy || (policy && !policy.get('dashboardID'));
        },
        renderer: (model) => {
          const policy = model.get('policy');

          // TODO in theory hidden ensures that policy is always present, however there may be race conditions
          return policy ? (
            <DashboardLink as="MenuItem" key="dashboard" withIcon id={policy.get('dashboardID')} alertId={model.id} />
          ) : null;
        }
      },
      {
        id: 'debug',
        icon: AiFillBug,
        label: 'Debug Alert',
        handler: this.handleDebug
      }
    ];
  }

  onHandleComment = (model) => {
    this.setState({ commentModel: model });
  };

  renderMetricColumn = (model) => {
    // NMS Alert
    if (model.isNmsPolicy) {
      return <NmsNativeAlertMetricListRenderer alertModel={model} numMetricsToShow={2} />;
    }

    if (model.isEventPolicy) {
      return metricLabelRenderer(model.policy.metrics[0], null, {
        round: true,
        fontWeight: 'medium',
        p: '2px 6px'
      });
    }

    const metricToValue = model.get('metricToValue') || {};

    return model.sortedMetrics.slice(0, 1).map((metricKey) => {
      const metricStorageEntry = model.policyMeasurement?.storage?.Metrics[metricKey] || {};
      const metricFormat = formatMetricValue(metricToValue[metricKey], metricKey);
      return <Text key={metricKey}>{`${metricFormat.value} ${metricStorageEntry.Label || metricFormat.metric}`}</Text>;
    });
  };

  handleViewDetails = (model) => {
    window.open(model.alertDetailLink, '_blank');
  };

  handleOnSort = (sortState) => {
    const { $exports } = this.props;

    $exports.setHash({ sortState });
  };

  handleDebug = (model) => {
    this.setState({ debugModel: model });
  };

  handleAckAlertDialog = (model) => {
    this.setState({ ackAlertModel: model });
  };

  handleClearAlertDialog = (model) => {
    this.setState({ clearAlertModel: model });
  };

  handleSubmitClearAlert = (formValues) => {
    const { $comments, $alerting } = this.props;
    const { clearAlertModel } = this.state;
    const { comment } = formValues || {};

    this.handleClearAlertDialog(null);

    $alerting.clearAlarms(clearAlertModel, { comment, toast: true }).then(() => {
      $comments.collection.fetch({ force: true });
    });
  };

  handleSubmitAckAlert = (formValues) => {
    const { $comments, $alerting } = this.props;
    const { ackAlertModel } = this.state;
    const { comment, autoAck } = formValues || {};

    this.handleAckAlertDialog(null);

    $alerting.acknowledgeAlarm(ackAlertModel, { comment, autoAck, toast: true }).then(() => {
      $comments.collection.fetch({ force: true });
    });
  };

  render() {
    const { $alerting, $app, useVirtualizedTable, onRowClick, tableProps, collection } = this.props;
    const { debugModel, ackAlertModel, clearAlertModel, commentModel } = this.state;

    return (
      <>
        <AdminTable
          guardForDuplicates
          noVirtualizedTable={useVirtualizedTable === false || $app.isExport}
          collection={collection || $alerting.collection}
          defaultColumns={this.defaultColumns}
          columns={this.columns}
          fetchCollectionOnMount={false}
          kebabActions={this.kebabActions}
          maxVisibleColumns={11}
          onRowClick={onRowClick}
          onSort={this.handleOnSort}
          rowAlign="center"
          rowHeight={ROW_HEIGHT}
          searchBar={false}
          showColumnSelector={!$app.isExport}
          columnSelectorPosition="toolbar"
          tabPrefId="alertManagerAlertingTable"
          showRefreshButton={!$app.isExport}
          showAutoRefresh={!$app.isExport}
          {...tableProps}
        />
        {!!debugModel && (
          <AlertDebugDialog isOpen={!!debugModel} alertModel={debugModel} onClose={() => this.handleDebug(null)} />
        )}
        {!!ackAlertModel && (
          <AckAlertDialog
            isOpen={!!ackAlertModel}
            alertModel={ackAlertModel}
            onClose={() => this.handleAckAlertDialog(null)}
            onSubmit={this.handleSubmitAckAlert}
          />
        )}
        {!!clearAlertModel && (
          <ClearAlertDialog
            isOpen={!!clearAlertModel}
            alertModels={[clearAlertModel]}
            onClose={() => this.handleClearAlertDialog(null)}
            onSubmit={this.handleSubmitClearAlert}
          />
        )}
        <CommentDialog
          entityType="alert"
          isOpen={!!commentModel}
          model={commentModel}
          onClose={() => this.onHandleComment(null)}
          onComment={() => this.onHandleComment(null)}
        />
      </>
    );
  }
}

export default AlertingTable;
